import { Fragment } from "react";
import { useSelector } from "react-redux";
import { CanvasChildrenProps } from "../../../components/Canvas/CanvasContainer";
import {
  selectDeviceListForD3, selectMaxNumberOfDevices,
  selectRangeWithUnits,
  selectStepCount,
} from "../../../store/freq-analysis/selectors";
import { convertToHz } from "../../../helpers/converters/frequency";
import DeviceModel from "../../../models/DeviceModel";
import { rangeIntersection } from "../../../helpers/math";

type DeviceDataInOneStepProps = {} & CanvasChildrenProps;

function getDeviceCountFromRange(deviceList: DeviceModel[], frequencyFrom: number, frequencyTo: number) {
  let count = 0;

  deviceList.forEach((device) => {
    if (
      (device.configuration?.wirelessInterfaces || []).some(
        (wirelessInterface) => {
          const bandwidth = convertToHz(wirelessInterface.technologyConfiguration?.bandwidth || '');
          return (wirelessInterface.channels || []).some((channel) => {
            const { centralFrequencyHz } = channel;
            const bottomValue = centralFrequencyHz - bandwidth / 2;
            const topValue = centralFrequencyHz + bandwidth / 2;
            return !!rangeIntersection(frequencyFrom, frequencyTo, bottomValue, topValue);
          })
        })
    ) {
      count++;
    }
  });

  return count;
}

const DeviceDataInOneStep = ({ containerHeight, containerWidth }: DeviceDataInOneStepProps) => {
  const dividerAmount = useSelector(selectStepCount);
  const maxNumberOfDevices = useSelector(selectMaxNumberOfDevices) || 0;
  const range = useSelector(selectRangeWithUnits);
  const deviceList = useSelector(selectDeviceListForD3);
  const from = convertToHz(range[0]);
  const to = convertToHz(range[1]);

  if (!containerWidth || !dividerAmount) {
    return null;
  }

  const step = (to - from) / dividerAmount;
  const pxInStep = containerWidth / dividerAmount;

  return (
    <g className='device-data'>
      <text
        x={20}
        y={25}
        fontSize="10px"
      >
        Number of devices
      </text>
      {
        new Array(dividerAmount).fill(1).map((_, index) => {
          const start = from + step * index;
          const end = start + step;
          const deviceCount = getDeviceCountFromRange(deviceList, start, end);
          const isError = maxNumberOfDevices && maxNumberOfDevices <= deviceCount;
          const isWarning = maxNumberOfDevices && maxNumberOfDevices * 0.7 <= deviceCount;
          const deviceNumberStringLength = deviceCount.toString().length;

          return deviceCount ? (
            <Fragment key={index}>
              {
                isError ? (
                  <rect
                    transform={`translate(${pxInStep * index}, 0)`}
                    width={pxInStep}
                    height={containerHeight}
                    fill="rgba(227, 27, 12, 0.4)"
                  />
                ) : null
              }
              {
                isWarning && !isError ? (
                  <rect
                    transform={`translate(${pxInStep * index}, 0)`}
                    width={pxInStep}
                    height={containerHeight}
                    fill="rgba(255, 152, 0, 0.4)"
                  />
                ) : null
              }
              <g transform={`translate(${pxInStep * index}, 25)`}>
                <text
                  fill="currentColor"
                  y="5"
                  dy="1em"
                  style={{ transform: `translateX(${(pxInStep - 9 * deviceNumberStringLength) / 2}px)` }}
                >
                  {deviceCount}
                </text>
              </g>
            </Fragment>
          ) : null
        })
      }
    </g>
  );
}

export default DeviceDataInOneStep;